import { IProductGroup } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { ProposedArticleLineViewModel } from 'src/app/business/care-proposal/view-models/proposed-article-line-view-model';
import { IntegratedCareProposalViewModel } from 'src/app/business/integrated-care/integrated-care-proposal';
import { BaseProductGroupViewModel } from 'src/app/business/product-group/base-product-group-view-model';
import { ProductGroupDB } from 'src/app/common/repository/databases';
import { QueryService } from 'src/app/shared/services/query/query.service';

@Injectable({ providedIn: 'root' })
export class ProductGroupLoadService {
  constructor(private _queryService: QueryService) {}

  public async loadProductGroups<T extends BaseProductGroupViewModel>(
    productGroupIds: string[],
    mapFn: (baseProductGroupViewModel: IProductGroup) => T
  ): Promise<T[]> {
    if (!productGroupIds || !productGroupIds.length) {
      return [];
    }
    const productGroups = await this._queryService.search<IProductGroup>(
      {
        query: productGroupIds.map(productGroupId => `_id:${productGroupId}`).join(' '),
      },
      ProductGroupDB,
      { isIn: true }
    );

    const parentsOfProductGroups = await this.loadParentProductGroups(productGroups);

    productGroups.push(...parentsOfProductGroups);

    return productGroups.map(mapFn);
  }

  public async loadProductGroupsNotInStandard<T extends BaseProductGroupViewModel>(
    integratedCareProposal: IntegratedCareProposalViewModel,
    productGroupsInStandardCareProposal: BaseProductGroupViewModel[],
    mapFn: (productGroup: IProductGroup) => T
  ): Promise<T[]> {
    // überprüfe, ob für jedes proposedArticleLine die entsprechende ProductGroup vorhanden ist?!
    const articleLines = integratedCareProposal.proposedArticleLines as ProposedArticleLineViewModel[];
    const nonPresentProductGroupIds = articleLines
      .filter(
        proposedArticleLine =>
          proposedArticleLine?.therapyProductGroupId &&
          !productGroupsInStandardCareProposal.some(
            productGroup => productGroup._id === proposedArticleLine.therapyProductGroupId
          )
      )
      .map(proposedArticleLine => {
        if (!integratedCareProposal.nonDefaultArticleProductGroupRelations[proposedArticleLine.therapyProductGroupId]) {
          integratedCareProposal.nonDefaultArticleProductGroupRelations[proposedArticleLine.therapyProductGroupId] = [
            proposedArticleLine.articleId,
          ];
        } else {
          integratedCareProposal.nonDefaultArticleProductGroupRelations[proposedArticleLine.therapyProductGroupId].push(
            proposedArticleLine.articleId
          );
        }
        return proposedArticleLine.therapyProductGroupId;
      });

    if (!nonPresentProductGroupIds.length) {
      return null;
    }

    productGroupsInStandardCareProposal.push(...(await this.loadProductGroups(nonPresentProductGroupIds, mapFn)));
  }

  private async loadParentProductGroups(productGroups: IProductGroup[]) {
    const parents: IProductGroup[] = [];

    let uniqueParentIds = [
      ...new Set(
        productGroups.filter(productGroup => !!productGroup.parentId).map(productGroup => productGroup.parentId)
      ),
    ];
    while (uniqueParentIds.length) {
      const parentProductGroups = await this._queryService.search<IProductGroup>(
        { query: uniqueParentIds.map(id => `_id:${id}`).join(' ') },
        ProductGroupDB,
        { isIn: true }
      );

      uniqueParentIds = [
        ...new Set(
          parentProductGroups.filter(productGroup => !!productGroup.parentId).map(productGroup => productGroup.parentId)
        ),
      ];

      parents.push(...parentProductGroups);
    }

    return parents;
  }
}
